import { useEffect, useRef, useState } from 'react';
import { arrayUnion, collection, doc, onSnapshot, orderBy, query, setDoc, updateDoc } from 'firebase/firestore';
import { PaperPlaneRight, X } from '@phosphor-icons/react';
import { db } from '../../firebase';
import { chatSessionOf, chatText, keepChat, linesOfSession } from '../../utils/liveRoomUi';

/**
 * The call's own chat, for this call only. A lesson's lines live on its
 * call's session (liveCallBookings/{id}/sessions/{sessionId}/messages,
 * UI/UX's), where the server also posts a line to the pair's thread while
 * only one of them is in the call (decision 132, onLiveCallChatLine) and
 * marks it postedToThread; once both are in, a line stays in the call. A
 * call with no lesson session (a thread's call) keeps its lines on the
 * room's signalling document (webrtc_rooms/{roomId}.chat), each with its
 * session, the panel showing only this call's. Each line also goes over the
 * room's data channel once written, so the other side sees it at once, not
 * when its listener catches up.
 */
export interface RoomChatLine { id: string; from: string; name: string; text: string; at: string; session?: string;
  /** The server posted it to the pair's thread too (decision 132). */
  postedToThread?: boolean }

export function subscribeRoomChat(roomId: string, bookingId: string | undefined, sessionId: string | null | undefined,
  onChange: (lines: RoomChatLine[], session: string | null) => void): () => void {
  if (bookingId && sessionId) {
    const q = query(collection(db, 'liveCallBookings', bookingId, 'sessions', sessionId, 'messages'), orderBy('at'));
    return onSnapshot(q, (snap) => {
      const lines = snap.docs.map((d) => {
        const x = d.data();
        return { id: d.id, from: x.from, name: x.name, text: x.text, at: x.at, session: sessionId, ...(x.postedToThread ? { postedToThread: true } : {}) } as RoomChatLine;
      });
      onChange(keepChat(lines), sessionId);
    }, () => onChange([], sessionId));
  }
  return onSnapshot(doc(db, 'webrtc_rooms', roomId), (snap) => {
    const data = snap.data();
    const session = chatSessionOf(bookingId, data?.callSessionId as string | undefined);
    const raw = (data?.chat ?? []) as RoomChatLine[];
    onChange(keepChat(linesOfSession([...raw].sort((a, b) => a.at.localeCompare(b.at)), session)), session);
  }, () => onChange([], null));
}

/**
 * Writes a line to the room and returns it, for the caller to send over the
 * room's data channel too (the other side shows whichever copy comes first).
 */
export async function sendRoomChat(roomId: string, session: string | null, line: Omit<RoomChatLine, 'id' | 'at' | 'session'>,
  lesson?: { bookingId: string; sessionId: string }): Promise<RoomChatLine | null> {
  if (lesson) {
    const ref = doc(collection(db, 'liveCallBookings', lesson.bookingId, 'sessions', lesson.sessionId, 'messages'));
    const at = new Date().toISOString();
    await setDoc(ref, { sessionId: lesson.sessionId, from: line.from, name: line.name.slice(0, 60), text: line.text, at });
    return { ...line, id: ref.id, at, session: lesson.sessionId };
  }
  if (!session) return null;
  const at = new Date().toISOString();
  const full: RoomChatLine = { ...line, id: `${at}-${line.from}`, at, session };
  await updateDoc(doc(db, 'webrtc_rooms', roomId), { chat: arrayUnion(full) });
  return full;
}

const timeOf = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

/**
 * The panel: a column beside the tiles on a larger screen, a sheet over
 * them on a phone. Chat at the top, the lines, then the field and Send. A
 * line the server also posted to the pair's thread (decision 132) says so.
 */
export function RoomChatPanel({ lines, me, onSend, onClose }: {
  lines: RoomChatLine[];
  me: string;
  onSend: (text: string) => Promise<void>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [lines.length]);

  const send = async () => {
    const text = chatText(draft);
    if (!text || sending) return;
    setSending(true);
    setError(null);
    try {
      await onSend(text);
      setDraft('');
    } catch {
      setError('Could not send. Please try again.');
    } finally { setSending(false); }
  };

  return (
    <div className="flex h-full w-full flex-col bg-call-tile text-white md:rounded-[16px]" data-room-chat>
      <div className="flex h-12 flex-shrink-0 items-center gap-3 px-4">
        <span className="text-[15px] leading-[20px] font-semibold">Chat</span>
        <span className="flex-1" />
        <button type="button" onClick={onClose} aria-label="Close chat"
          className="ios-press flex h-11 w-11 cursor-pointer items-center justify-center rounded-full text-white/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
          <X size={18} weight="bold" aria-hidden="true" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-2" role="log" aria-live="polite" aria-label="Chat">
        {lines.length === 0 && (
          <p className="pt-6 text-center text-[13px] leading-[18px] text-white/50">Only you two see this. Until you're both here, it also goes to your messages.</p>
        )}
        {lines.map((l) => {
          const mine = l.from === me;
          return (
            <div key={l.id} className={`mt-2 flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
              <span className="px-1 text-[11px] leading-[13px] text-white/50">{mine ? 'You' : l.name} · {timeOf(l.at)}</span>
              <span className={`mt-0.5 max-w-[85%] break-words rounded-[16px] px-3 py-1.5 text-[15px] leading-[20px] ${mine ? 'bg-biscayne text-white' : 'bg-white/[0.14] text-white'}`}>
                {l.text}
              </span>
              {l.postedToThread && <span className="px-1 pt-0.5 text-[11px] leading-[13px] text-white/50" data-posted-to-thread>Also sent to Messages</span>}
            </div>
          );
        })}
        <div ref={end} />
      </div>
      {error && <p className="px-4 pb-1 text-[13px] leading-[18px] text-[#FF8A80]">{error}</p>}
      <form className="flex flex-shrink-0 items-center gap-2 px-3 pb-3 pt-1" onSubmit={(e) => { e.preventDefault(); void send(); }}
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Message" aria-label="Chat message"
          className="h-11 min-w-0 flex-1 rounded-[100px] bg-white/[0.14] px-4 text-[15px] leading-[20px] text-white placeholder:text-white/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40" />
        <button type="submit" aria-label="Send" disabled={!chatText(draft) || sending}
          className="ios-press flex h-11 w-11 flex-shrink-0 cursor-pointer items-center justify-center rounded-full bg-biscayne text-white disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40">
          <PaperPlaneRight size={18} weight="fill" aria-hidden="true" />
        </button>
      </form>
    </div>
  );
}
